import { useEffect, useState } from "react";
import Draggable from "react-draggable";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import {
  Button,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import swal from "sweetalert";
import { LoadingSpinner, isUrdu } from "../../../../constants/const";
import { GetLanguageString } from "../../../helper/Components";
import { GetSelectedLanguage } from "../../../helper/Method";
import useForm from "../../../shared/useForm";
import { ApiFinance } from "../ApiFinance";

const EditInfaqHead = ({
  setEditModal,
  setReload,
  reload,
  infaqHeadValues,
}) => {
  const [loading, setLoading] = useState(false);
  const [infaqHeadsList, setInfaqHeadsList] = useState([]);
  const [selectedInfaqHead, setSelectedInfaqHead] = useState(
    infaqHeadValues.infaqHead
  );
  const { t } = useTranslation();
  const [errors, setErrors] = useState({
    title: "",
    infaqHead: "",
  });
  const { values, handleInputChange } = useForm(infaqHeadValues);
  const toggle = () => setEditModal(false);

  const [position, setPosition] = useState({ x: 0, y: 0 });

  const handleDrag = (e, ui) => {
    const { x, y } = position;
    setPosition({ x: x + ui.deltaX, y: y + ui.deltaY });
  };

  useEffect(() => {
    setLoading(true);
    ApiFinance()
      .GetFinanceInfaqHeadsListFromFinanceSoftware()
      .then((res) => {
        setInfaqHeadsList(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        swal({
          title: err,
          text: err.response.data,
          icon: "error",
          buttons: "OK",
        });
        setLoading(false);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [infaqHeadValues.id]);

  const validate = () => {
    let temp = {};
    temp.title = values.title ? "" : t("settings_title_error_message");
    temp.infaqHead =
      selectedInfaqHead !== null
        ? ""
        : t("finance_tab_infaq_heads_add_infaq_head_error");
    setErrors({
      ...temp,
    });

    return Object.values(temp).every((x) => x === "");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = { ...values, head: selectedInfaqHead };
    setLoading(true);

    ApiFinance()
      .UpdateFinanceInfaqHead(payload)
      .then(() => {
        swal({
          title: t("finance_infaq_head_updated"),
          className: GetSelectedLanguage() === "en" ? "" : "ur",
          icon: "success",
          buttons: "OK",
        });
        setReload(!reload);
        toggle();
      })
      .catch((err) => {
        if (
          err.response &&
          err.response.data === "finance_infaq_head_already_exists"
        ) {
          swal({
            title: t(err.response.data), // translates using i18n
            className: GetSelectedLanguage() === "en" ? "" : "ur",
            icon: "error",
            buttons: "OK",
          });
        } else {
          swal({
            title: err.message || "Something went wrong",
            text: err.response?.data || "",
            icon: "error",
            buttons: "OK",
          });
        }
        toggle();
        console.error(err);
      })
      .finally(() => setLoading(false));
  };

  return (
    <div>
      <Draggable position={position} onDrag={handleDrag} cancel=".no-drag">
        <Modal isOpen={true} className="inmodal" autoFocus={false}>
          <ModalHeader className="modal-title" tag="h4">
            {<GetLanguageString props="finance_tab_infaq_heads_edit" />}
          </ModalHeader>
          {!loading ? (
            <Form onSubmit={handleSubmit}>
              <ModalBody>
                {" "}
                <FormGroup>
                  <Label className="col-form-label">
                    {
                      <GetLanguageString props="finance_tab_infaq_heads_select" />
                    }
                  </Label>
                  <Select
                    value={selectedInfaqHead}
                    options={infaqHeadsList}
                    className="basic-single no-drag"
                    classNamePrefix="select"
                    onChange={(v) => {
                      setSelectedInfaqHead(v);
                      setErrors({
                        ...errors,
                        infaqHead: "",
                      });
                    }}
                  />
                  {errors.infaqHead && (
                    <h4 className="text-error">{errors.infaqHead}</h4>
                  )}
                </FormGroup>
                <FormGroup>
                  <Label>{<GetLanguageString props="settings_title" />}</Label>
                  <Input
                    autoFocus
                    type="text"
                    name="title"
                    className={isUrdu(values.title) ? "ur no-drag" : "no-drag"}
                    value={values.title}
                    onChange={handleInputChange}
                    {...(errors.title && { invalid: true })}
                  />
                  {errors.title && (
                    <FormFeedback>
                      <h4>{errors.title}</h4>
                    </FormFeedback>
                  )}
                </FormGroup>{" "}
                <FormGroup>
                  <Label>
                    {<GetLanguageString props="common_description" />}
                  </Label>
                  <Input
                    type="text"
                    name="description"
                    className={
                      isUrdu(values.description) ? "ur no-drag" : "no-drag"
                    }
                    value={values.description}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </ModalBody>
              <ModalFooter>
                <Button color="white" onClick={toggle}>
                  {<GetLanguageString props="common_cancel" />}
                </Button>
                <Button color="primary" type="submit">
                  {<GetLanguageString props="common_save_changes" />}
                </Button>
              </ModalFooter>
            </Form>
          ) : (
            LoadingSpinner()
          )}
        </Modal>
      </Draggable>
    </div>
  );
};

export default EditInfaqHead;
